import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid, GraduationCap, Calendar, Trophy, Shield, LogOut,
  Menu, X, Hash, Users, Hammer, Compass, MessageSquare, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, LevelBadge } from "@/components/common";
import { NotificationBell } from "@/components/NotificationBell";
import api from "@/lib/api";

const primaryNav = [
  { to: "/feed", label: "Community", icon: LayoutGrid, testid: "nav-community" },
  { to: "/matches", label: "Discover", icon: Compass, testid: "nav-matches" },
  { to: "/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages" },
  { to: "/courses", label: "Courses", icon: GraduationCap, testid: "nav-courses" },
  { to: "/build", label: "Build", icon: Hammer, testid: "nav-build" },
  { to: "/events", label: "Events", icon: Calendar, testid: "nav-events" },
  { to: "/members", label: "Members", icon: Users, testid: "nav-members" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = () => api.get("/spaces").then((r) => setSpaces(r.data)).catch(() => {});
    load();
    window.addEventListener("spaces-changed", load);
    return () => window.removeEventListener("spaces-changed", load);
  }, []);

  useEffect(() => {
    const load = () => api.get("/conversations")
      .then((r) => setUnread(r.data.reduce((a, c) => a + (c.unread || 0), 0))).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 text-sm font-sans font-medium rounded-md transition-colors ${
      isActive
        ? "text-cobalt bg-secondary"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
    }`;

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-6 border-b border-border flex items-center justify-between">
        <button
          onClick={() => { navigate("/feed"); setMobileOpen(false); }}
          className="flex items-center gap-2.5 text-left"
          data-testid="sidebar-wordmark"
        >
          <img src="/ikigora-icon.png" alt="Ikigora" className="w-9 h-9 shrink-0 object-contain" />
          <span>
            <span className="block font-serif text-xl font-extrabold leading-none tracking-tight">Ikigora</span>
            <span className="block eyebrow text-cobalt mt-1">Community Guild</span>
          </span>
        </button>
        <NotificationBell />
      </div>

      <nav className="flex-1 overflow-y-auto py-4 space-y-6">
        <div className="space-y-1">
          {primaryNav.map((item) => (
            <NavLink key={item.to} to={item.to} className={navClass} data-testid={item.testid}
              onClick={() => setMobileOpen(false)}>
              <item.icon size={16} strokeWidth={1.75} />
              <span className="flex-1">{item.label}</span>
              {item.to === "/messages" && unread > 0 && (
                <span className="bg-cobalt text-white text-[10px] font-mono rounded-full px-1.5 py-0.5" data-testid="nav-messages-unread">{unread}</span>
              )}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink to="/admin" className={navClass} data-testid="nav-admin"
              onClick={() => setMobileOpen(false)}>
              <Shield size={16} strokeWidth={1.75} />
              Admin
            </NavLink>
          )}
        </div>

        <div>
          <div className="px-4 mb-2 flex items-center justify-between">
            <span className="eyebrow text-muted-foreground">Spaces</span>
            <NavLink to="/spaces" data-testid="nav-browse-spaces" onClick={() => setMobileOpen(false)}
              className="text-[11px] text-cobalt hover:underline">Browse all</NavLink>
          </div>
          <div className="space-y-1">
            {spaces.map((s) => (
              <NavLink key={s.space_id} to={`/space/${s.slug}`} className={navClass}
                data-testid={`nav-space-${s.slug}`} onClick={() => setMobileOpen(false)}>
                <Hash size={14} strokeWidth={1.75} />
                <span className="truncate flex-1">{s.name}</span>
                {s.subscribed && <span className="w-1.5 h-1.5 rounded-full bg-cobalt shrink-0" title="Subscribed" data-testid={`space-subbed-dot-${s.slug}`} />}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      <div className="border-t border-border p-3 space-y-2">
        {user && user.plan !== "pro" && (
          <button onClick={() => { navigate("/upgrade"); setMobileOpen(false); }} data-testid="sidebar-upgrade-button"
            className="w-full flex items-center justify-center gap-2 py-2 rounded-md bg-cobalt text-white text-sm font-semibold hover:bg-gold-hover transition-colors">
            <Sparkles size={15} /> Go Pro
          </button>
        )}
        {user && (
          <button
            onClick={() => { navigate(`/members/${user.user_id}`); setMobileOpen(false); }}
            className="w-full flex items-center gap-3 p-2 hover:bg-secondary/50 transition-colors text-left"
            data-testid="sidebar-profile-link"
          >
            <Avatar src={user.picture} name={user.name} size={36} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground font-mono">{user.points} pts</div>
            </div>
          </button>
        )}
        <button onClick={logout} data-testid="logout-button"
          className="w-full flex items-center justify-center gap-2 py-2 border border-border text-sm hover:bg-secondary/50 transition-colors">
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="w-64 border-r border-border shrink-0 hidden md:block sticky top-0 h-screen bg-paper">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 border-b border-border bg-paper">
        <button onClick={() => navigate("/feed")} className="font-serif text-xl font-bold">Ikigora</button>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" className="p-2">
            <Menu size={20} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative w-72 max-w-[80%] bg-paper border-r border-border h-full">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 z-10" data-testid="mobile-menu-close">
              <X size={20} />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 dot-grid pt-14 md:pt-0">
        <BetaBanner />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8">{children}</div>
      </main>
    </div>
  );
}

function BetaBanner() {
  const [show, setShow] = useState(() => localStorage.getItem("ikigora_beta_dismissed") !== "1");
  if (!show) return null;
  return (
    <div className="bg-cobalt text-white px-4 py-2.5 flex items-center justify-center gap-3 text-center" data-testid="beta-banner">
      <Sparkles size={15} className="shrink-0" />
      <p className="text-xs sm:text-sm font-medium">Early beta is now launched — some functionality is still in development.</p>
      <button onClick={() => { localStorage.setItem("ikigora_beta_dismissed", "1"); setShow(false); }}
        data-testid="beta-banner-dismiss" className="ml-2 opacity-80 hover:opacity-100 transition-opacity shrink-0">
        <X size={15} />
      </button>
    </div>
  );
}
